import {
  ByVehicleAgeAgeRequest,
  CountResponse,
  VehicleAgeTimeline,
} from "shared/api/api";
import {
  useCountClaimsAssociatedVehicles,
  useListClaimsByVehicleAgeTimeline,
  useListClaimsCount,
} from "shared/api/claims/hooks";
import { UseAPIState, UsePaginatedAPIState } from "shared/api/hooks";
import {
  useListSignalEventsByVehicleAgeTimeline,
  useListSignalEventsOccurrencesCount,
  useSignalEventsAssociatedVehiclesCount,
} from "shared/api/signalEvents/hooks";
import { CountRequestWithVehiclesFilter } from "shared/api/vehicles/api";
import { DEFAULT_MILEAGE_UNIT } from "shared/constants";
import { EventTypeEnum, MileageUnit } from "shared/types";
import { randomID } from "shared/utils";

import Events from "pages/Dashboard/components/Events";
import { MainTableComponentProps } from "pages/Dashboard/types";
import { LandingPageViewConfig } from "pages/shared/Dashboard/types";

import { FilterRowState } from "features/ui/Filters/FilterBuilder/types";
import { FilterOperator } from "features/ui/Filters/types";

export const ROWS_PER_PAGE = 25;

export const MAIN_FIELDS_COLUMN_ID = "__MAIN_FIELDS__";

export const LANDING_PAGE_TAB_KEY = (
  tab: LandingPageViewConfig,
  suffix?: string
) => {
  const suffixString = suffix ? `_${suffix}` : "";
  if (tab.versionKey) {
    return `landing_page_tab_${tab.versionKey}${suffixString}_v1`;
  }

  return `landing_page_tab_${tab.eventType ?? tab.eventType}_${tab.title}${suffixString}_v1`;
};

export const DASHBOARD_VIEW_ANALYTICS_PAGE_KEY = (
  config: LandingPageViewConfig,
  suffix?: string
) =>
  `dashboard_view_analytics_${config.title}_${config.versionKey ?? ""}_${suffix ?? ""}`;

export const EVENT_TABLE_MAPPING: Partial<
  Record<EventTypeEnum, (props: MainTableComponentProps) => JSX.Element>
> = {
  [EventTypeEnum.CLAIM]: (props) => (
    <Events {...props} eventType={EventTypeEnum.CLAIM} />
  ),
  [EventTypeEnum.SIGNAL_EVENT]: (props) => (
    <Events {...props} eventType={EventTypeEnum.SIGNAL_EVENT} />
  ),
};

export const EVENT_COUNT_HOOK_MAPPING: Partial<
  Record<
    EventTypeEnum,
    (
      props: CountRequestWithVehiclesFilter
    ) => UsePaginatedAPIState<CountResponse>
  >
> = {
  [EventTypeEnum.CLAIM]: useListClaimsCount,
  [EventTypeEnum.SIGNAL_EVENT]: useListSignalEventsOccurrencesCount,
};

export interface VehicleCountHookProps {
  filter: string;
  vehicleFilter: string;
  mileageUnit?: MileageUnit;
}

export const VEHICLE_COUNT_HOOK_MAPPING: Partial<
  Record<
    EventTypeEnum,
    (props: VehicleCountHookProps) => UseAPIState<CountResponse>
  >
> = {
  [EventTypeEnum.CLAIM]: ({
    filter,
    vehicleFilter,
    mileageUnit,
  }: VehicleCountHookProps) =>
    useCountClaimsAssociatedVehicles({
      filter: vehicleFilter,
      claimFilter: filter,
      mileageUnit: mileageUnit ?? DEFAULT_MILEAGE_UNIT,
    }),
  [EventTypeEnum.SIGNAL_EVENT]: ({
    filter,
    vehicleFilter,
  }: VehicleCountHookProps) =>
    useSignalEventsAssociatedVehiclesCount({
      filter: vehicleFilter,
      signalEventOccurrencesFilter: filter,
    }),
};

export interface ByVehicleAgeHookProps extends ByVehicleAgeAgeRequest {
  vehicleFilter: string;
  eventFilter: string;
  hideCosts?: boolean;
}

export const BY_VEHICLE_AGE_HOOK_MAPPING: Partial<
  Record<
    EventTypeEnum,
    (props: ByVehicleAgeHookProps) => UseAPIState<VehicleAgeTimeline[]>
  >
> = {
  [EventTypeEnum.CLAIM]: ({
    vehicleFilter,
    eventFilter,
    byVehicleAgeExposure,
    byVehicleAgeBirthday,
    granularity,
    hideCosts,
  }) =>
    useListClaimsByVehicleAgeTimeline({
      vehiclesFilter: vehicleFilter,
      claimsFilter: eventFilter,
      byVehicleAgeExposure,
      byVehicleAgeBirthday,
      granularity,
      hideCosts: hideCosts != null ? hideCosts : true,
    }),
  [EventTypeEnum.SIGNAL_EVENT]: ({
    vehicleFilter,
    eventFilter,
    byVehicleAgeExposure,
    byVehicleAgeBirthday,
    granularity,
  }) =>
    useListSignalEventsByVehicleAgeTimeline({
      vehiclesFilter: vehicleFilter,
      signalEventOccurrencesFilter: eventFilter,
      byVehicleAgeExposure,
      byVehicleAgeBirthday,
      granularity,
    }),
};

export const EVENT_TYPE_LABEL: Partial<Record<EventTypeEnum, string>> = {
  [EventTypeEnum.CLAIM]: "Claims",
  [EventTypeEnum.SIGNAL_EVENT]: "Events",
};

export const filterWithSearch: FilterRowState = {
  id: randomID(),
  type: "row",
  attribute: "search",
  operator: FilterOperator.CONTAINS,
  values: [],
};

export const X_AXIS_COLOR = "#e5e7eb";
export const X_AXIS_FONT_COLOR = "#6f6f6f";
